import { z } from 'zod';

/**
 * The entities a school can bring in from a CSV template (scoping 3.4), in the order the
 * migration applies them: people first, then structure, then what links them.
 */
export const importEntitySchema = z.enum([
  'students',
  'guardians',
  'teachers',
  'subjects',
  'groups',
  'enrollments',
  'opening-balances',
]);

/**
 * Query of `GET /imports/templates/{entity}`: the template with its example rows, or the same
 * columns filled with the school's current data (`fill=tenant`) so a rep can fix and re-import.
 */
export const importTemplateQuerySchema = z.object({
  fill: z.enum(['examples', 'tenant']).default('examples'),
});

/**
 * What the school decided, in the review, for rows the file could not place (QA PM-04): the
 * school year for rows whose `ano_lectivo` is empty (instead of the active one, or when there
 * is none), and per group as written in the file, an existing group to use or `null` to import
 * those rows without a group. The dry run and the background import apply the same decisions.
 */
export const importPlacementsSchema = z.object({
  schoolYearId: z.string().uuid().optional(),
  groups: z
    .array(
      z.object({
        name: z.string().trim().min(1).max(120),
        groupId: z.string().uuid().nullable(),
      }),
    )
    .max(500)
    .default([]),
});

/**
 * The placement decisions of one file.
 */
export type ImportPlacements = z.output<typeof importPlacementsSchema>;

/**
 * Body of `POST /imports/dry-run`: one file, base64-encoded so an Excel export in Latin-1 or
 * UTF-16 survives the trip. Files up to a few thousand rows are validated synchronously; the
 * background import (#61) takes the larger ones.
 */
export const importDryRunInputSchema = z.object({
  entity: importEntitySchema,
  content: z.string().min(1).max(4_000_000),
  filename: z.string().trim().max(200).optional(),
  placements: importPlacementsSchema.optional(),
});

/**
 * An importable entity.
 */
export type ImportEntity = z.infer<typeof importEntitySchema>;
/**
 * Query of `GET /imports/templates/{entity}`.
 */
export type ImportTemplateQuery = z.infer<typeof importTemplateQuerySchema>;
/**
 * Body of `POST /imports/dry-run`.
 */
export type ImportDryRunInput = z.input<typeof importDryRunInputSchema>;

/**
 * One column of a template, documented in Spanish for the school.
 */
export type ImportColumnDoc = {
  key: string;
  header: string;
  required: boolean;
  description: string;
  example: string;
};

/**
 * A template as the app downloads it: the column docs and the CSV text (UTF-8 with BOM,
 * `;` separator, so Excel in Colombia opens it directly).
 */
export type ImportTemplate = {
  entity: ImportEntity;
  title: string;
  description: string;
  filename: string;
  columns: ImportColumnDoc[];
  csv: string;
  rows: number;
};

/**
 * The list of templates, for the setup wizard.
 */
export type ImportTemplateSummary = Pick<
  ImportTemplate,
  'entity' | 'title' | 'description' | 'filename'
> & { order: number };

/**
 * One problem in one cell (or one row when `column` is absent), in Spanish.
 */
export type ImportRowIssue = { column?: string; message: string };

/**
 * The outcome of one row of the file. `line` is the row number as Excel shows it (the header
 * is row 1). `action` says what applying the file would do with the row.
 */
export type ImportRowResult = {
  line: number;
  status: 'ok' | 'error';
  action?: 'create' | 'update';
  key?: string;
  issues: ImportRowIssue[];
  warnings: ImportRowIssue[];
};

/**
 * What the dry run answers: how the file was read, which columns matched, and every row.
 */
export type ImportDryRunResult = {
  entity: ImportEntity;
  /**
   * Rows the file could not place, for the review to resolve (students and enrollments only;
   * `null` for the other templates).
   */
  placement: ImportPlacementGaps | null;
  encoding: 'utf-8' | 'utf-16le' | 'windows-1252';
  separator: ';' | ',' | '\t';
  columns: { matched: string[]; missing: string[]; unknown: string[] };
  fileErrors: string[];
  totals: {
    rows: number;
    ok: number;
    errors: number;
    warnings: number;
    create: number;
    update: number;
  };
  rows: ImportRowResult[];
};

/**
 * What a file could not place: rows that need a school year when the school has no active one
 * (and none was chosen), and groups written in the file that do not exist in the year, with the
 * grade the rows name (and its id when that grade exists) and the rows that use each.
 */
export type ImportPlacementGaps = {
  noActiveYear: number[];
  groups: {
    name: string;
    grade: string | null;
    gradeId: string | null;
    schoolYearId: string;
    lines: number[];
  }[];
};

/**
 * Body of `POST /imports`: the uploaded files (one per entity, keys from the presigned upload
 * under the school's `import` prefix). The worker applies them in template order whatever the
 * order given here.
 */
export const createImportInputSchema = z.object({
  files: z
    .array(
      z.object({
        entity: importEntitySchema,
        key: z.string().min(1).max(500),
        filename: z.string().trim().max(200).optional(),
        placements: importPlacementsSchema.optional(),
      }),
    )
    .min(1)
    .max(7)
    .refine(
      (files) => new Set(files.map((file) => file.entity)).size === files.length,
      'one file per entity',
    ),
});

/**
 * Body of `POST /imports`.
 */
export type CreateImportInput = z.input<typeof createImportInputSchema>;

/**
 * Lifecycle of an import run.
 */
export type ImportJobStatus = 'queued' | 'running' | 'done' | 'failed';

/**
 * What applying one file did: rows read, created, updated, left unchanged (idempotent
 * re-import), and rows that went to the error file.
 */
export type ImportFileCounts = {
  rows: number;
  created: number;
  updated: number;
  unchanged: number;
  errors: number;
};

/**
 * One file of an import run as stored on the job.
 */
export type ImportFileState = {
  entity: ImportEntity;
  key: string;
  filename: string;
  status: 'pending' | 'done' | 'failed';
  counts: ImportFileCounts | null;
  errorKey: string | null;
  fileErrors: string[];
  /**
   * The review's placement decisions, applied by the worker exactly as the dry run did.
   */
  placements?: ImportPlacements;
};

/**
 * An import run as the API answers it; `errorUrl` is a short-lived download link for the file
 * of rejected rows (present on `GET /imports/{id}` only).
 */
export type ImportJobView = {
  id: string;
  status: ImportJobStatus;
  files: (ImportFileState & { errorUrl?: string })[];
  error: string | null;
  requestedByUserId: string | null;
  actingStaffUserId: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
};

/**
 * The last import of a school, as tenant health shows it.
 */
export type ImportJobSummary = {
  id: string;
  status: ImportJobStatus;
  createdAt: string;
  finishedAt: string | null;
  rows: number;
  errors: number;
};
